/**
 * Phone field — intlTelInput
 * Visible [tel] field is for typing the national number; on submit its value
 * is replaced with the full international number (country code + number).
 * Optional hidden fields `phone-complete` / `country` are also synced if present.
 */
export function phoneMask() {
  const inputs = document.querySelectorAll('input[type="tel"]');
  if ( ! inputs.length || typeof window.intlTelInput === 'undefined' ) {
    return;
  }

  const isMobile = window.matchMedia('(max-width: 576px)').matches;
  const themeUrl = ( window.BA_DATA && window.BA_DATA.themeUrl ) || '';
  const utilsScript = themeUrl + '/assets/lib/intlTelInput/js/utils.js';

  // Geo-IP providers tried in order until one returns a valid ISO-2 country.
  // Each entry maps the provider's JSON to the country code field it uses.
  const GEO_PROVIDERS = [
    { url: 'https://ipwho.is/', pick: ( d ) => d && d.success !== false && d.country_code },
    { url: 'https://get.geojs.io/v1/ip/country.json', pick: ( d ) => d && d.country },
    { url: 'https://freeipapi.com/api/json', pick: ( d ) => d && d.countryCode },
    { url: 'https://ipapi.co/json/', pick: ( d ) => d && d.country_code },
  ];

  // Absolute last resort only if EVERY geo provider fails.
  const DEFAULT_COUNTRY = 'us';

  // Fetch one provider with a timeout; resolves to a lowercase ISO-2 code.
  const fetchCountry = ( url, pick ) => {
    const controller = new AbortController();
    const timer = setTimeout( () => controller.abort(), 3000 );
    return fetch( url, { signal: controller.signal } )
      .then( ( res ) => ( res.ok ? res.json() : Promise.reject() ) )
      .then( ( data ) => {
        clearTimeout( timer );
        const code = pick( data );
        if ( ! code || typeof code !== 'string' || code.length !== 2 ) {
          return Promise.reject();
        }
        return code.toLowerCase();
      } )
      .catch( ( err ) => {
        clearTimeout( timer );
        return Promise.reject( err );
      } );
  };

  // Try providers sequentially, falling through to the next on any failure.
  const lookupCountry = () =>
    GEO_PROVIDERS.reduce(
      ( chain, provider ) => chain.catch( () => fetchCountry( provider.url, provider.pick ) ),
      Promise.reject()
    );

  // Format typed digits according to the plugin's example-number placeholder
  // (e.g. UA "50 123 4567" -> keeps the same grouping/separators).
  const formatByMask = ( digits, mask ) => {
    let out = '';
    let di = 0;
    for ( let i = 0; i < mask.length && di < digits.length; i++ ) {
      if ( /\d/.test( mask[ i ] ) ) {
        out += digits[ di++ ];
      } else {
        out += mask[ i ];
      }
    }
    if ( di < digits.length ) {
      out += digits.slice( di );
    }
    return out;
  };

  inputs.forEach( ( input ) => {
    const iti = window.intlTelInput( input, {
      useFullscreenPopup: isMobile,
      initialCountry: 'auto',
      autoPlaceholder: 'aggressive',
      customPlaceholder: function(selectedCountryPlaceholder, selectedCountryData) {
        if(selectedCountryData.dialCode === '380'){
          selectedCountryPlaceholder = '50 123 4567'
        }
        return selectedCountryPlaceholder;
      },
      separateDialCode: true,
      geoIpLookup: ( callback ) => {
        // Reuse a successful lookup for the rest of the session so we don't
        // keep hitting the rate-limited endpoints.
        let cached = null;
        try {
          cached = sessionStorage.getItem('ba_geo_cc');
        } catch ( e ) {}
        if ( cached ) {
          callback( cached );
          return;
        }

        lookupCountry()
          .then( ( code ) => {
            try {
              sessionStorage.setItem('ba_geo_cc', code);
            } catch ( e ) {}
            callback( code );
          } )
          .catch( () => callback( DEFAULT_COUNTRY ) );
      },
      utilsScript: utilsScript,
    } );

    // Live formatting that mirrors the placeholder's format
    input.addEventListener('input', () => {
      const mask = input.placeholder;
      if ( ! mask || ! /\d/.test( mask ) ) {
        return;
      }
      const digits = input.value.replace( /\D/g, '' );
      const formatted = formatByMask( digits, mask );
      if ( formatted !== input.value ) {
        input.value = formatted;
      }
    } );

    const form = input.closest('form');
    if ( ! form ) {
      return;
    }

    form.addEventListener('submit', () => {
      const full = iti.getNumber();
      if ( full ) {
        input.value = full;
      }
      const complete = form.querySelector('input[name="phone-complete"]');
      const country = form.querySelector('input[name="country"]');
      if ( complete ) {
        complete.value = full;
      }
      if ( country ) {
        const data = iti.getSelectedCountryData();
        country.value = data && data.name ? data.name : '';
      }
    }, true );
  } );
}
